import { useEffect, useState, useCallback, useMemo } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { adminApi } from "@/lib/api";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import {
  Users, UserCheck, Ban, DollarSign, Search, RefreshCw, Trash2,
  CircleSlash, CheckCircle2, KeyRound, Copy, Filter, ArrowUpDown, X, Award, Gift, Eye,
} from "lucide-react";
import SetPasswordModal from "./SetPasswordModal";

function initials(name = "", email = "") {
  const src = (name || email || "?").trim();
  if (!src) return "?";
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const d = Math.floor(diff / 86400000);
  if (d === 0) return "today";
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  if (d < 365) return `${Math.floor(d / 30)}mo ago`;
  return `${Math.floor(d / 365)}y ago`;
}

const STATS_TILES = [
  { key: "total", label: "TOTAL USERS", icon: Users, bg: "bg-slate-50 border-slate-200", ring: "bg-slate-200 text-slate-800", num: "text-slate-900" },
  { key: "active", label: "ACTIVE", icon: UserCheck, bg: "bg-emerald-50 border-emerald-200", ring: "bg-emerald-200 text-emerald-800", num: "text-emerald-700" },
  { key: "suspended", label: "SUSPENDED", icon: Ban, bg: "bg-rose-50 border-rose-200", ring: "bg-rose-200 text-rose-800", num: "text-rose-700" },
  { key: "total_paid_out", label: "TOTAL PAID OUT", icon: DollarSign, bg: "bg-sky-50 border-sky-200", ring: "bg-sky-200 text-sky-800", num: "text-sky-700", currency: true },
];

const STATUS_TABS = [
  { v: "all", label: "All", tone: "slate" },
  { v: "active", label: "Active", tone: "emerald" },
  { v: "suspended", label: "Suspended", tone: "rose" },
];

const SORTS = [
  { v: "joined_desc", label: "Newest first" },
  { v: "joined_asc", label: "Oldest first" },
  { v: "submissions_desc", label: "Most submissions" },
  { v: "earned_desc", label: "Highest earned" },
];

export default function AdminUsers() {
  const [data, setData] = useState({ items: [], stats: {} });
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState("joined_desc");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [pwdFor, setPwdFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort };
      if (query.trim()) params.q = query.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      const r = await adminApi.get("/admin/user-accounts", { params });
      setData(r.data);
    } catch { toast.error("Couldn't load user accounts"); }
    finally { setLoading(false); }
  }, [query, statusFilter, sort]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
  }, [load]);

  const toggleSuspend = async (row) => {
    const path = row.status === "active" ? "suspend" : "unsuspend";
    try {
      await adminApi.post(`/admin/user-accounts/${row.id}/${path}`);
      toast.success(row.status === "active" ? "Account suspended" : "Account reactivated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const remove = async (row) => {
    if (!window.confirm(`Permanently delete ${row.email}?\nSubmissions are kept but unlinked from the account.`)) return;
    try {
      await adminApi.delete(`/admin/user-accounts/${row.id}`);
      toast.success("Account deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const copyEmail = (email) => {
    navigator.clipboard?.writeText(email);
    toast.success(`Copied ${email}`);
  };
  const bulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const verb = action === "delete" ? "delete" : action;
    if (!window.confirm(`${verb.toUpperCase()} ${ids.length} account${ids.length > 1 ? "s" : ""}?`)) return;
    try {
      const r = await adminApi.post("/admin/user-accounts/bulk-action", { ids, action });
      toast.success(`${r.data.action} → ${r.data.affected}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk action failed"); }
  };

  const items = data.items || [];
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.id));
  const toggleAll = () => allSelected ? setSelected(new Set()) : setSelected(new Set(items.map((i) => i.id)));
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Identify the top earner in the current view
  const topEarnerId = useMemo(() => {
    if (!items.length) return null;
    const top = [...items].sort((a, b) => b.paid_total - a.paid_total)[0];
    return top && top.paid_total > 0 ? top.id : null;
  }, [items]);

  return (
    <AdminLayout>
      <Toaster richColors position="top-center" />
      <div className="p-6 md:p-8" data-testid="admin-users">

        {/* Title */}
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] font-black tracking-wider uppercase text-indigo-600">User accounts</span>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-1" data-testid="admin-users-title">Registered customers</h1>
            <p className="text-slate-500 mt-1 text-sm">Manage sign-ups, credits, and suspensions.</p>
            <div className="mt-3 h-[3px] w-24 rounded-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
          </div>
        </div>

        {/* Stats hero */}
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {STATS_TILES.map((t) => {
            const raw = data.stats?.[t.key] ?? 0;
            const val = t.currency
              ? `$${Number(raw).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
              : Number(raw).toLocaleString();
            const Icon = t.icon;
            return (
              <div key={t.key} data-testid={`users-stat-${t.key}`} className={`rounded-2xl border p-5 ${t.bg}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${t.ring}`}><Icon size={16} strokeWidth={2.5} /></div>
                  <span className="text-[10px] font-black tracking-wider text-slate-500">{t.label}</span>
                </div>
                <div className={`mt-3 text-3xl font-black tabular-nums ${t.num}`}>{val}</div>
              </div>
            );
          })}
        </div>

        {/* Toolbar */}
        <div className="mt-6 bg-white border border-slate-200 rounded-2xl shadow-sm px-4 py-3 flex flex-col md:flex-row md:items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, email, or phone…" data-testid="users-search"
              className="w-full h-11 pl-10 pr-4 rounded-xl bg-slate-50 border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:bg-white" />
          </div>
          {/* Status tabs */}
          <div className="inline-flex rounded-xl border border-slate-200 bg-slate-50 p-1">
            {STATUS_TABS.map((t) => (
              <button key={t.v} onClick={() => setStatusFilter(t.v)} data-testid={`users-tab-${t.v}`}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-black transition ${
                  statusFilter === t.v ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
                }`}>
                <Filter size={12} /> {t.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="users-sort"
              className="appearance-none h-11 pl-9 pr-8 rounded-xl border border-slate-200 bg-white text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-500">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
            <ArrowUpDown className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <button onClick={load} disabled={loading} data-testid="users-refresh"
            className="inline-flex items-center gap-2 h-11 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>

        {/* Bulk bar */}
        {selected.size > 0 && (
          <div className="sticky top-[72px] z-20 mt-4 flex flex-wrap items-center gap-2 bg-slate-900 text-white rounded-2xl px-5 py-3 shadow-lg" data-testid="users-bulk-bar">
            <span className="text-sm font-semibold">{selected.size} selected</span>
            <div className="h-5 w-px bg-white/20 mx-2" />
            <button onClick={() => bulk("suspend")} data-testid="users-bulk-suspend" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-amber-500 hover:bg-amber-600 text-xs font-semibold">
              <CircleSlash className="w-3.5 h-3.5" /> Suspend
            </button>
            <button onClick={() => bulk("unsuspend")} data-testid="users-bulk-unsuspend" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-xs font-semibold">
              <CheckCircle2 className="w-3.5 h-3.5" /> Reactivate
            </button>
            <button onClick={() => bulk("delete")} data-testid="users-bulk-delete" className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-white/10 hover:bg-rose-600 text-xs font-semibold">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
            <button onClick={() => setSelected(new Set())} className="ml-auto inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold text-white/70 hover:text-white hover:bg-white/10">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </div>
        )}

        {/* Table */}
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-4 w-10">
                    <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="users-select-all" className="rounded border-slate-300" />
                  </th>
                  <th className="text-left px-2 py-4 font-black">User</th>
                  <th className="text-left px-3 py-4 font-black">Email</th>
                  <th className="text-right px-3 py-4 font-black">Subs</th>
                  <th className="text-right px-3 py-4 font-black">Earned</th>
                  <th className="text-right px-3 py-4 font-black">Credit</th>
                  <th className="text-left px-3 py-4 font-black">Referrals</th>
                  <th className="text-left px-3 py-4 font-black">Status</th>
                  <th className="text-left px-3 py-4 font-black">Joined · Active</th>
                  <th className="text-right px-5 py-4 font-black">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const isTop = r.id === topEarnerId;
                  return (
                    <tr key={r.id} data-testid={`user-row-${r.email}`} className="border-t border-slate-100 hover:bg-slate-50/60 transition">
                      <td className="px-5 py-4"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} className="rounded border-slate-300" /></td>
                      <td className="px-2 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`relative w-9 h-9 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${
                            isTop ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white" : "bg-slate-900 text-white"
                          }`}>
                            {initials(r.name, r.email)}
                            {isTop && <Award size={12} className="absolute -top-1 -right-1 text-amber-500 bg-white rounded-full p-0.5" />}
                          </div>
                          <div className="min-w-0">
                            <div className="font-black text-slate-900 truncate">{r.name || "—"}</div>
                            {r.phone && <div className="text-[11px] text-slate-400 font-mono">{r.phone}</div>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        <div className="flex items-center gap-1.5 group">
                          <span className="text-slate-700 truncate max-w-[220px]">{r.email}</span>
                          <button onClick={() => copyEmail(r.email)} data-testid={`user-copy-${r.email}`} title="Copy email"
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition">
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        {r.referral_code && <div className="text-[10px] text-indigo-600 font-mono font-bold mt-0.5">ref: {r.referral_code}</div>}
                      </td>
                      <td className="px-3 py-4 text-right font-black tabular-nums text-slate-900">{r.submissions}</td>
                      <td className="px-3 py-4 text-right font-black tabular-nums text-emerald-700">${r.paid_total.toFixed(2)}</td>
                      <td className="px-3 py-4 text-right font-mono tabular-nums text-slate-700">${r.credit_balance.toFixed(2)}</td>
                      <td className="px-3 py-4">
                        <div className="inline-flex items-center gap-1.5 text-slate-700">
                          <Gift size={12} className="text-indigo-500" />
                          <span className="font-black tabular-nums">{r.referrals}</span>
                        </div>
                      </td>
                      <td className="px-3 py-4">
                        {r.status === "active" ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3" /> ACTIVE
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black tracking-wider bg-rose-100 text-rose-700 border border-rose-200">
                            <CircleSlash className="w-3 h-3" /> SUSPENDED
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="text-xs text-slate-700 font-medium">{fmtDate(r.joined)}</div>
                        <div className="text-[10px] text-slate-400">last activity {fmtRelative(r.last_activity)}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link to={`/admin/submissions?q=${encodeURIComponent(r.email)}`}
                            title="View this customer's submissions" data-testid={`user-view-subs-${r.email}`}
                            className="p-2 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"><Eye className="w-4 h-4" /></Link>
                          <button onClick={() => setPwdFor(r)} data-testid={`user-set-password-${r.email}`}
                            title="Set new password"
                            className="p-2 rounded-lg text-indigo-600 hover:text-white hover:bg-indigo-600 transition"><KeyRound className="w-4 h-4" /></button>
                          <button onClick={() => toggleSuspend(r)} data-testid={`user-suspend-${r.email}`}
                            title={r.status === "active" ? "Suspend" : "Reactivate"}
                            className={`p-2 rounded-lg transition ${r.status === "active" ? "text-amber-600 hover:text-white hover:bg-amber-500" : "text-emerald-600 hover:text-white hover:bg-emerald-500"}`}>
                            {r.status === "active" ? <CircleSlash className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                          </button>
                          <button onClick={() => remove(r)} data-testid={`user-delete-${r.email}`} title="Delete"
                            className="p-2 rounded-lg text-rose-600 hover:text-white hover:bg-rose-600 transition"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!items.length && !loading && (
                  <tr><td colSpan={10} className="py-16 text-center text-slate-400 text-sm">
                    {query || statusFilter !== "all" ? "No accounts match your filters." : "No registered accounts yet."}
                  </td></tr>
                )}
                {loading && !items.length && <tr><td colSpan={10} className="py-16 text-center text-slate-400 text-sm">Loading…</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {pwdFor && <SetPasswordModal user={pwdFor} onClose={() => setPwdFor(null)} />}
    </AdminLayout>
  );
}
